import { Button, Divider, Drawer, Group, Input, InputBase, Space, Stack, Text, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconPencil, IconPlus, IconX } from "@tabler/icons-react"
import { useProgramRegistrationState } from "../../state/programregistration.state";
import { useProgramPromotionCodesQuery } from "../../api/promotion.api";
import { ProgramPromotionCode } from "../../models/promotions.models";
import { useState } from "react";
import { Combobox, useCombobox } from "@mantine/core";
import { formatDiscountAmount } from "../../functions/programregistration.functions";
import { useTransactionByIdQuery } from "../../api/transaction.api";

const DiscountEditor = () => {

	const { programRegistration, updateProgramRegistration, status } = useProgramRegistrationState();
	const [opened, { open, close }] = useDisclosure(false);
	const { data: promotionCodes } = useProgramPromotionCodesQuery(programRegistration.Data.ProgramGroupId ?? 0);
	const [selectedPromotionCode, setSelectedPromotionCode] = useState<ProgramPromotionCode | null>(null);
	const { data: transaction, isLoading: isLoadingTransaction } = useTransactionByIdQuery(programRegistration.TransactionId ?? 0);
	
	const canAdd = status.canEdit && !programRegistration.Data.PromotionCode && !status.isCancelled && (programRegistration.TransactionId == 0 || transaction && transaction.Paid == 0) && !isLoadingTransaction;
	const canEdit = status.canEdit && transaction == null;
	const canRemove = status.canEdit && programRegistration.Data.PromotionCode && !status.isCancelled && (programRegistration.TransactionId == 0 || transaction && transaction.Paid == 0) && !isLoadingTransaction;

	const handleAddPromotionCode = () => {	
		if (selectedPromotionCode) {
			programRegistration.Data.PromotionCode = {
				Id: selectedPromotionCode.Id,
				ProgramId: programRegistration.Data.ProgramId,
				Code: selectedPromotionCode.Code,
				DiscountType: selectedPromotionCode.DiscountType,
				DiscountAmount: selectedPromotionCode.DiscountAmount
			};

			updateProgramRegistration(programRegistration);
		}
		close();
	}

	const handleEditPromotionCode = () => {
		if (!promotionCodes || promotionCodes.length === 0) {
			return;
		}
		const selectedCode = promotionCodes.find(pc => pc.Id === programRegistration.Data.PromotionCode?.Id) ?? null;
		setSelectedPromotionCode(selectedCode);
		open();
	}

	const handleRemovePromotionCode = () => {
		setSelectedPromotionCode(null);
		programRegistration.Data.PromotionCode = undefined;
		programRegistration.Data.PromotionCodeId = 0;
		updateProgramRegistration(programRegistration);
		close();
	}

	return <>
		{canAdd && <Button onClick={open} variant="light" size="xs" leftSection={<IconPlus />}>Add</Button>}
		{!canAdd && <Button onClick={handleEditPromotionCode} variant="light" size="xs" leftSection={<IconPencil />} disabled={!canEdit}>Edit</Button>}

		<Drawer opened={opened} onClose={close} title={<div><Title order={3}>Promotion Code</Title></div>} position="right" size="md" withCloseButton>
			<Text>Select a promotion code to apply to this registration:</Text>
			<Space h="md" />
			<PromotionCodeCombobox promotionCodes={promotionCodes ?? []}
				value={selectedPromotionCode}
				onChange={setSelectedPromotionCode} />

			<Divider mt="lg" mb="xs" />

			<Group mt="md" justify="space-between">
				<Group gap="xs">
					<Button type="button" onClick={handleAddPromotionCode} disabled={!selectedPromotionCode} leftSection={<IconCheck />}>
						Apply
					</Button>
					{canRemove && <>
					<Button type="button" variant="light" color="red" leftSection={<IconX />} onClick={handleRemovePromotionCode}>
						Remove
					</Button>
					</>}
				</Group>
				<Button type="button" variant="subtle" onClick={close}>
					Cancel
				</Button>
			</Group>
		</Drawer>
	</>
}

export default DiscountEditor

type PromotionCodeComboboxProps = {
	promotionCodes: ProgramPromotionCode[];
	value: ProgramPromotionCode | null;
	onChange: (promotionCode: ProgramPromotionCode | null) => void;
};

export const PromotionCodeCombobox = ({
	promotionCodes,
	value,
	onChange,
}: PromotionCodeComboboxProps) => {
	const combobox = useCombobox();

	return (
		<Combobox
			store={combobox}
			onOptionSubmit={(val) => {
				const selected = promotionCodes.find((pc) => pc.Id.toString() === val) ?? null;
				onChange(selected);
				combobox.closeDropdown();
			}}
		>
			<Combobox.Target>
				<InputBase
					component="button"
					type="button"
					pointer
					rightSection={<Combobox.Chevron />}
					onClick={() => combobox.toggleDropdown()}
					rightSectionPointerEvents="none"
					multiline
				>
					{value ? (
						<Group justify="space-between" align="center" wrap="nowrap">
							<Stack gap={0}>
								<Text fw={500} component="code">{value.Code}</Text>
								<Text size="xs" c="dimmed">
									{value.Description}
								</Text>
							</Stack>
							<Text size="sm">{formatDiscountAmount(value)} Discount</Text>
						</Group>
					) : (
						<Input.Placeholder>Pick value</Input.Placeholder>
					)}
				</InputBase>
			</Combobox.Target>
			<Combobox.Dropdown>
				<Combobox.Options style={{ maxHeight: 300, overflowY: 'auto' }}>
					{promotionCodes.map((pc) => (
						<Combobox.Option key={pc.Id} value={pc.Id.toString()}>
							<Group justify="space-between" wrap="nowrap">
								<Stack gap={0}>
									<Text fw={500} component="code">{pc.Code}</Text>
									<Text size="xs" c="dimmed">
										{pc.Description}
									</Text>
								</Stack>
								<Text size="sm">{formatDiscountAmount(pc)} Discount</Text>
							</Group>
						</Combobox.Option>
					))}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};